import type {
  Guest,
  AddGuestRequest,
  AddGuestResponse,
  SearchGuestsResponse,
  GetGuestsResponse,
  DeleteGuestResponse,
  FloorPlan,
  FloorPlanResponse,
  SaveFloorPlanRequest,
  SaveFloorPlanResponse,
  RegisterRequest,
  LoginRequest,
  AuthResponse,
  VerifyTokenResponse,
  CreateEventRequest,
  CreateEventResponse,
  GetEventsResponse,
  GetEventResponse,
  DeleteEventResponse,
  CreateLimitRequestRequest,
  CreateLimitRequestResponse,
  GetLimitRequestsResponse,
  GetAllUsersResponse,
  UpdateUserLimitsRequest,
  UpdateUserLimitsResponse,
  ResolveLimitRequestRequest,
  ResolveLimitRequestResponse
} from './types';
import sampleGuests from './data/sampleGuests.json';

const API_BASE = '/api';

// In-memory storage for local development
let localGuests: Guest[] = [...sampleGuests.guests.map(g => ({ ...g, eventId: 'default' }))];
let localToken: string | null = null;
let localUser: any = null;
let localEvents: any[] = [{ id: 'default', userId: 'local', name: 'Sample Event', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }];

// Default floor plan for local development
let localFloorPlan: FloorPlan = {
  id: 'default',
  eventId: 'default',
  name: 'Default Venue Layout',
  width: 800,
  height: 600,
  tables: [
    {
      id: '1',
      x: 100,
      y: 100,
      width: 80,
      height: 80,
      rotation: 0,
      shape: 'round',
      tableNumber: 1,
      capacity: 6,
      color: '#f8f9fa'
    },
    {
      id: '2',
      x: 250,
      y: 100,
      width: 80,
      height: 80,
      rotation: 0,
      shape: 'round',
      tableNumber: 2,
      capacity: 6,
      color: '#f8f9fa'
    },
    {
      id: '3',
      x: 400,
      y: 100,
      width: 80,
      height: 80,
      rotation: 0,
      shape: 'round',
      tableNumber: 3,
      capacity: 6,
      color: '#f8f9fa'
    }
  ],
  background: '#ffffff',
  createdAt: '2024-01-01T00:00:00.000Z',
  updatedAt: '2024-01-01T00:00:00.000Z'
};

// Helper function to check if we're in development mode
const isDevelopment = () => {
  return window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.hostname.includes('192.168.') ||
         window.location.hostname.includes('10.') ||
         window.location.hostname.includes('172.');
};

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token') || localToken;
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Auth API Functions
export async function register(data: RegisterRequest): Promise<AuthResponse> {
  try {
    if (isDevelopment()) {
      localUser = { id: 'local', email: data.email, name: data.name, eventCount: 0, eventLimit: 1, guestLimit: 20 };
      localToken = btoa(JSON.stringify({ userId: 'local', email: data.email }));
      localStorage.setItem('auth_token', localToken);
      return { success: true, token: localToken, user: localUser };
    }

    const response = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    const result = await response.json();
    if (result.success && result.token) {
      localStorage.setItem('auth_token', result.token);
    }
    return result;
  } catch (error) {
    return { success: false, error: 'Failed to register. Please try again.' };
  }
}

export async function login(data: LoginRequest): Promise<AuthResponse> {
  try {
    if (isDevelopment()) {
      localUser = { id: 'local', email: data.email, name: 'Local User', eventCount: 1, eventLimit: 1, guestLimit: 20 };
      localToken = btoa(JSON.stringify({ userId: 'local', email: data.email }));
      localStorage.setItem('auth_token', localToken);
      return { success: true, token: localToken, user: localUser };
    }

    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    const result = await response.json();
    if (result.success && result.token) {
      localStorage.setItem('auth_token', result.token);
    }
    return result;
  } catch (error) {
    return { success: false, error: 'Failed to login. Please try again.' };
  }
}

export async function verifyToken(): Promise<VerifyTokenResponse> {
  try {
    if (isDevelopment()) {
      if (localToken || localStorage.getItem('auth_token')) {
        return {
          success: true,
          user: localUser || { id: 'local', email: 'local@example.com', name: 'Local User', eventCount: 1, eventLimit: 1, guestLimit: 20 }
        };
      }
      return { success: false, error: 'No token' };
    }

    const response = await fetch(`${API_BASE}/auth/verify`, {
      headers: getAuthHeaders(),
    });

    return await response.json();
  } catch (error) {
    return { success: false, error: 'Failed to verify token.' };
  }
}

export function logout() {
  localStorage.removeItem('auth_token');
  localToken = null;
  localUser = null;
}

// Event API Functions
export async function createEvent(data: CreateEventRequest): Promise<CreateEventResponse> {
  try {
    if (isDevelopment()) {
      // Check event limit in development mode
      if (localEvents.length >= 1) {
        return { 
          success: false, 
          error: 'You have reached the maximum limit of 1 event. Delete your existing event to create a new one.' 
        };
      }

      const newEvent = {
        id: crypto.randomUUID(),
        userId: 'local',
        name: data.name,
        description: data.description,
        date: data.date,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      localEvents.push(newEvent);
      return { success: true, event: newEvent };
    }

    const response = await fetch(`${API_BASE}/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(data),
    });

    return await response.json();
  } catch (error) {
    return { success: false, error: 'Failed to create event. Please try again.' };
  }
}

export async function getEvents(): Promise<GetEventsResponse> {
  try {
    if (isDevelopment()) {
      return { success: true, events: localEvents };
    }

    const response = await fetch(`${API_BASE}/events`, {
      headers: getAuthHeaders(),
    });

    return await response.json();
  } catch (error) {
    return { success: false, error: 'Failed to get events. Please try again.' };
  }
}

export async function getEvent(eventId: string): Promise<GetEventResponse> {
  try {
    if (isDevelopment()) {
      const event = localEvents.find(e => e.id === eventId);
      if (event) {
        return { success: true, event };
      }
      return { success: false, error: 'Event not found' };
    }

    // Public endpoint - no auth required for guest search
    const response = await fetch(`${API_BASE}/events/${eventId}`);

    return await response.json();
  } catch (error) {
    return { success: false, error: 'Failed to get event. Please try again.' };
  }
}

export async function deleteEvent(eventId: string): Promise<DeleteEventResponse> {
  try {
    if (isDevelopment()) {
      const index = localEvents.findIndex(e => e.id === eventId);
      if (index !== -1) {
        localEvents.splice(index, 1);
        // Also delete associated guests
        localGuests = localGuests.filter(g => g.eventId !== eventId);
        return { success: true };
      }
      return { success: false, error: 'Event not found' };
    }

    const response = await fetch(`${API_BASE}/events/${eventId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    return await response.json();
  } catch (error) {
    return { success: false, error: 'Failed to delete event. Please try again.' };
  }
}

// Guest API Functions (updated to include eventId)
export async function addGuest(guest: AddGuestRequest): Promise<AddGuestResponse> {
  try {
    if (isDevelopment()) {
      // Check guest limit in development mode
      const eventGuests = localGuests.filter(g => g.eventId === guest.eventId);
      if (eventGuests.length >= 20) {
        return {
          success: false,
          error: 'You have reached the maximum limit of 20 guests for this event'
        };
      }

      const newGuest: Guest = {
        id: crypto.randomUUID(),
        eventId: guest.eventId,
        name: guest.name.trim(),
        table: guest.table,
        createdAt: new Date().toISOString()
      };
      
      localGuests.push(newGuest);
      
      return {
        success: true,
        guest: newGuest
      };
    }

    const response = await fetch(`${API_BASE}/guests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(guest),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: 'Failed to add guest. Please try again.',
    };
  }
}

export async function searchGuests(query: string, eventId: string): Promise<SearchGuestsResponse> {
  try {
    if (isDevelopment()) {
      if (!query || query.trim().length < 2) {
        return {
          success: true,
          results: []
        };
      }
      
      const searchQuery = query.toLowerCase();
      const results = localGuests
        .filter(guest => guest.eventId === eventId && guest.name.toLowerCase().includes(searchQuery))
        .map(guest => ({
          guest,
          highlight: guest.name
        }))
        .sort((a, b) => {
          const aExact = a.guest.name.toLowerCase() === searchQuery;
          const bExact = b.guest.name.toLowerCase() === searchQuery;
          
          if (aExact && !bExact) return -1;
          if (!aExact && bExact) return 1;
          
          return a.guest.name.localeCompare(b.guest.name);
        })
        .slice(0, 10);
      
      return {
        success: true,
        results
      };
    }

    const response = await fetch(`${API_BASE}/guests/search?q=${encodeURIComponent(query)}&eventId=${eventId}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: 'Failed to search guests. Please try again.',
    };
  }
}

export async function getGuests(eventId: string): Promise<GetGuestsResponse> {
  try {
    if (isDevelopment()) {
      return {
        success: true,
        guests: localGuests.filter(g => g.eventId === eventId).sort((a, b) => a.name.localeCompare(b.name))
      };
    }

    const response = await fetch(`${API_BASE}/guests?eventId=${eventId}`);
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: 'Failed to load guests. Please try again.',
    };
  }
}

export async function deleteGuest(id: string, eventId: string): Promise<DeleteGuestResponse> {
  try {
    if (isDevelopment()) {
      const index = localGuests.findIndex(guest => guest.id === id && guest.eventId === eventId);
      if (index !== -1) {
        localGuests.splice(index, 1);
        return {
          success: true
        };
      }
      return {
        success: false,
        error: 'Guest not found'
      };
    }

    const response = await fetch(`${API_BASE}/guests/${id}?eventId=${eventId}`, {
      method: 'DELETE',
    });
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: 'Failed to delete guest. Please try again.',
    };
  }
}

// Floor Plan API Functions (updated to include eventId)
export async function getFloorPlan(eventId: string): Promise<FloorPlanResponse> {
  try {
    if (isDevelopment()) {
      return {
        success: true,
        floorPlan: { ...localFloorPlan, eventId }
      };
    }

    const response = await fetch(`${API_BASE}/floorplan?eventId=${eventId}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error in getFloorPlan:', error);
    return {
      success: false,
      error: 'Failed to load floor plan. Please try again.',
    };
  }
}

export async function saveFloorPlan(request: SaveFloorPlanRequest): Promise<SaveFloorPlanResponse> {
  try {
    if (isDevelopment()) {
      localFloorPlan = {
        ...request.floorPlan,
        id: localFloorPlan.id,
        eventId: request.floorPlan.eventId,
        createdAt: localFloorPlan.createdAt,
        updatedAt: new Date().toISOString()
      };

      return {
        success: true,
        floorPlan: localFloorPlan
      };
    }

    const response = await fetch(`${API_BASE}/floorplan`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: 'Failed to save floor plan. Please try again.',
    };
  }
}

// Limit Increase Request API Functions
export async function createLimitRequest(data: CreateLimitRequestRequest): Promise<CreateLimitRequestResponse> {
  try {
    if (isDevelopment()) {
      // Simulate success in development
      return { success: true, request: {
        id: crypto.randomUUID(),
        userId: 'local',
        userEmail: 'local@example.com',
        userName: 'Local User',
        requestedEventLimit: data.requestedEventLimit,
        requestedGuestLimit: data.requestedGuestLimit,
        currentEventLimit: 1,
        currentGuestLimit: 20,
        message: data.message,
        status: 'pending',
        createdAt: new Date().toISOString()
      }};
    }

    const response = await fetch(`${API_BASE}/limit-requests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(data),
    });

    return await response.json();
  } catch (error) {
    return { success: false, error: 'Failed to create limit request. Please try again.' };
  }
}

export async function getLimitRequests(): Promise<GetLimitRequestsResponse> {
  try {
    if (isDevelopment()) {
      return { success: true, requests: [] };
    }

    const response = await fetch(`${API_BASE}/limit-requests`);
    return await response.json();
  } catch (error) {
    return { success: false, error: 'Failed to get limit requests. Please try again.' };
  }
}

// SuperAdmin API Functions
export async function getAllUsers(password: string): Promise<GetAllUsersResponse> {
  try {
    if (isDevelopment()) {
      return { success: true, users: [{
        id: 'local',
        email: 'local@example.com',
        name: 'Local User',
        createdAt: new Date().toISOString(),
        eventCount: 1,
        eventLimit: 1,
        guestLimit: 20
      }]};
    }

    const response = await fetch(`${API_BASE}/superadmin/users`, {
      headers: {
        'X-SuperAdmin-Password': password
      }
    });

    return await response.json();
  } catch (error) {
    return { success: false, error: 'Failed to get users. Please try again.' };
  }
}

export async function updateUserLimits(password: string, data: UpdateUserLimitsRequest): Promise<UpdateUserLimitsResponse> {
  try {
    if (isDevelopment()) {
      return { success: true, user: {
        id: data.userId,
        email: 'local@example.com',
        name: 'Local User',
        createdAt: new Date().toISOString(),
        eventCount: 1,
        eventLimit: data.eventLimit,
        guestLimit: data.guestLimit
      }};
    }

    const response = await fetch(`${API_BASE}/superadmin/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-SuperAdmin-Password': password
      },
      body: JSON.stringify(data),
    });

    return await response.json();
  } catch (error) {
    return { success: false, error: 'Failed to update user limits. Please try again.' };
  }
}

export async function resolveLimitRequest(password: string, data: ResolveLimitRequestRequest): Promise<ResolveLimitRequestResponse> {
  try {
    if (isDevelopment()) {
      return { success: true };
    }

    const response = await fetch(`${API_BASE}/superadmin/resolve-request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-SuperAdmin-Password': password
      },
      body: JSON.stringify(data),
    });

    return await response.json();
  } catch (error) {
    return { success: false, error: 'Failed to resolve request. Please try again.' };
  }
}
